import { useCallback, useEffect, useRef, useState } from 'react';

export const SEARCH_COMPACT_SCROLL_TOP = 24;
export const SEARCH_COMPACT_SCROLL_DELTA = 4;

interface UseCompactMobileSearchOptions {
  isMobile: boolean;
  isSearchActive: boolean;
}

export const useCompactMobileSearch = ({
  isMobile,
  isSearchActive,
}: UseCompactMobileSearchOptions) => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isSearchCompact, setIsSearchCompact] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const lastScrollYRef = useRef(0);

  useEffect(() => {
    if (!isMobile) {
      setIsSearchCompact(false);
      setIsSearchExpanded(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || isSearchActive) return undefined;

    const onScroll = () => {
      const y = window.scrollY;

      if (isSearchExpanded) {
        if (y > lastScrollYRef.current + SEARCH_COMPACT_SCROLL_DELTA) {
          setIsSearchExpanded(false);
          setIsSearchCompact(true);
        }
      } else if (y <= SEARCH_COMPACT_SCROLL_TOP) {
        setIsSearchCompact(false);
      } else if (y > lastScrollYRef.current + SEARCH_COMPACT_SCROLL_DELTA) {
        setIsSearchCompact(true);
      }

      lastScrollYRef.current = y;
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isMobile, isSearchActive, isSearchExpanded]);

  useEffect(() => {
    if (
      isMobile &&
      !isSearchActive &&
      !isSearchExpanded &&
      window.scrollY > SEARCH_COMPACT_SCROLL_TOP
    ) {
      setIsSearchCompact(true);
    }
  }, [isMobile, isSearchActive, isSearchExpanded]);

  const showCompactSearch =
    isMobile && isSearchCompact && !isSearchExpanded && !isSearchActive;

  const openMobileSearch = useCallback(() => {
    setIsSearchExpanded(true);
    setIsSearchCompact(false);
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }, []);

  const handleMobileSearchBlur = useCallback((searchValue: string) => {
    if (!searchValue.trim() && window.scrollY > SEARCH_COMPACT_SCROLL_TOP) {
      setIsSearchExpanded(false);
      setIsSearchCompact(true);
    }
  }, []);

  return {
    searchInputRef,
    showCompactSearch,
    isSearchExpanded,
    openMobileSearch,
    handleMobileSearchBlur,
  };
};
